import { HttpClientModule } from '@angular/common/http';
import { ErrorHandler, NgModule } from '@angular/core';
import {
  DateAdapter, MAT_DATE_FORMATS, MAT_DATE_LOCALE, MatNativeDateModule
} from '@angular/material/core';
import { MAT_DIALOG_DEFAULT_OPTIONS, MatDialogConfig } from '@angular/material/dialog';
import {
  MAT_FORM_FIELD_DEFAULT_OPTIONS
} from '@angular/material/form-field';
import { BrowserModule, Title } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { ServiceWorkerModule } from '@angular/service-worker';
import { EffectsModule } from '@ngrx/effects';
import {
  FullRouterStateSerializer, NavigationActionTiming, StoreRouterConnectingModule
} from '@ngrx/router-store';
import { Store, StoreModule } from '@ngrx/store';
import { StoreDevtoolsModule } from '@ngrx/store-devtools';
import { AngularTokenModule, AngularTokenService } from '@raysuelzer/angular-token';
import { APOLLO_OPTIONS, ApolloModule } from 'apollo-angular';
import { HttpLink } from 'apollo-angular/http';
import { pairwise, skip } from 'rxjs/operators';

import { environment } from 'environments/environment';

import { authOptions } from 'auth/auth-options';
import { TermsDialogComponent } from 'auth/dialogs/terms-dialog/terms-dialog.component';
import {
  AcceptInvitationComponent
} from 'auth/pages/accept-invitation/accept-invitation.component';
import { ForgotPasswordComponent } from 'auth/pages/forgot-password/forgot-password.component';
import {
  InvitationInvalidComponent
} from 'auth/pages/invitation-invalid/invitation-invalid.component';
import { LoginBoxComponent } from 'auth/pages/login-box/login-box.component';
import { PasswordResetComponent } from 'auth/pages/password-reset/password-reset.component';
import { VerifyMfaComponent } from 'auth/pages/verify-mfa/verify-mfa.component';
import {
  DateFnsDateAdapter, MAT_DATE_FNS_DATE_FORMATS
} from 'core/date/material-datefns-date-adapter';
import {
  PersistantStateHookManager
} from 'core/state/persistant-state-hooks/persistant-state-hook-manager.service';
import { EntityCoreModule } from 'entity/entity-core.module';
import { SharedModule } from 'shared/shared.module';

import { AccountEffects } from './_state/account/account-effects';
import { CAMPAIGN_ID_CHANGED } from './_state/campaign-context/campaign-context-actions';
import { CampaignContextEffects } from './_state/campaign-context/campaign-context-effects';
import { reducers } from './_state/reducers';
import { selectQueryParam } from './_state/router/router-selectors';
import { AppRouterState } from './_state/router/router-state';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { ApolloErrorHandlerService } from './core/apollo-error-handler.service';
import { SentryErrorHandler } from './core/sentry-error-handler.service';
import { setupApollo } from './core/setup-apollo';
import { EntityMapViewEffects } from './entity-map-view/_state/entity-map-view.effects';
import { ExportModule } from './export/export.module';
import { HomeComponent } from './home/home.component';
import { AdminHeaderComponent } from './main/components/admin-header/admin-header.component';
import { AdminNavComponent } from './main/components/admin-nav/admin-nav.component';
import {
  CampaignHeaderComponent
} from './main/components/campaign-header/campaign-header.component';
import {
  CampaignSwitcherComponent
} from './main/components/campaign-switcher/campaign-switcher.component';
import {
  EntityQuickSearchComponent
} from './main/components/entity-quick-search/entity-quick-search.component';
import { FooterComponent } from './main/components/footer/footer.component';
import { HeaderNoTabsComponent } from './main/components/header-no-tabs/header-no-tabs.component';
import { HeaderSwitchComponent } from './main/components/header-switch/header-switch.component';
import { LogoComponent } from './main/components/logo.component';
import { PWADialogComponent } from './main/components/pwa-dialog/pwa-dialog.component';
import { SupportCardComponent } from './main/components/support-card/support-card.component';
import { SupportDialogComponent } from './main/components/support-dialog/support-dialog.component';
import {
  UserProfileCardComponent
} from './main/components/user-profile-card/user-profile-card.component';
import { NoCampaignsPage } from './main/pages/no-campaigns/no-campaigns.page';
import { NotFoundPage } from './main/pages/not-found/not-found.page';
import { SelectCampaignPage } from './main/pages/select-campaign/select-campaign.page';
import { NoteModule } from './note/note.module';
import { QueryModule } from './query-builder/query.module';
import { MAT_PAGINATOR_DEFAULT_OPTIONS } from '@angular/material/paginator';
import { AddressRefetchEffects } from './_state/entity/address-refetch.effects';
import { ContactTrackingModule } from './contact-tracking/contact-tracking.module';
import { AccountSetupMfaPage } from 'account/pages/account-setup-mfa/account-setup-mfa.page';

@NgModule({
  declarations: [
    AppComponent,
    HomeComponent,
    LogoComponent,
    SelectCampaignPage,
    // Auth,
    AcceptInvitationComponent,
    LoginBoxComponent,
    ForgotPasswordComponent,
    InvitationInvalidComponent,
    PasswordResetComponent,
    VerifyMfaComponent,
    TermsDialogComponent,
    AccountSetupMfaPage,
    // Header
    HeaderSwitchComponent,
    HeaderNoTabsComponent,
    AdminHeaderComponent,
    CampaignHeaderComponent,
    EntityQuickSearchComponent,
    AdminNavComponent,
    UserProfileCardComponent,
    CampaignSwitcherComponent,
    PWADialogComponent,
    SupportCardComponent,
    SupportDialogComponent,
    // Other
    NotFoundPage,
    NoCampaignsPage,
    FooterComponent
  ],
  imports: [
    BrowserModule,
    ApolloModule,
    ExportModule,
    BrowserAnimationsModule,
    MatNativeDateModule,
    SharedModule,
    NoteModule,
    QueryModule,
    EntityCoreModule,
    ContactTrackingModule,
    AngularTokenModule.forRoot(authOptions),
    AppRoutingModule,
    HttpClientModule,
    /**NGRX */
    StoreModule.forRoot(reducers, {
      runtimeChecks: {
        strictStateImmutability: false,
        strictActionImmutability: false,
      }
    }),
    EffectsModule.forRoot([
      CampaignContextEffects,
      AccountEffects,
      EntityMapViewEffects,
      AddressRefetchEffects
    ]),
    StoreDevtoolsModule.instrument({
      maxAge: 25,
      logOnly: environment.production, // Restrict extension to log-only mode
      connectInZone: true}),
    StoreRouterConnectingModule.forRoot({
      serializer: FullRouterStateSerializer,
      navigationActionTiming: NavigationActionTiming.PostActivation,
    }),
    ServiceWorkerModule.register('/ngsw-worker.js', { enabled: environment.production })
  ],
  providers: [
    Title,
    AngularTokenService,
    { provide: MAT_FORM_FIELD_DEFAULT_OPTIONS, useValue: { appearance: 'fill', hideRequiredMarker: true, subscriptSizing: 'dynamic' } },
    { provide: MAT_PAGINATOR_DEFAULT_OPTIONS, useValue: { formFieldApperance: 'fill' } },
    { provide: DateAdapter, useClass: DateFnsDateAdapter, deps: [MAT_DATE_LOCALE] },
    { provide: MAT_DATE_FORMATS, useValue: MAT_DATE_FNS_DATE_FORMATS },
    {
      provide: MAT_DIALOG_DEFAULT_OPTIONS,
      useValue: {
        ...new MatDialogConfig(),
        ...{ panelClass: 'material-modal', closeOnNavigation: true, maxWidth: null }
      }
    },
    {
      provide: ErrorHandler,
      useClass: SentryErrorHandler
    },
    {
      provide: APOLLO_OPTIONS,
      useFactory: (
        httpLink: HttpLink,
        apolloErrorHandlerService: ApolloErrorHandlerService
      ) =>
        setupApollo(httpLink, apolloErrorHandlerService),
      deps: [HttpLink, ApolloErrorHandlerService]
    }
  ],
  exports: [],
  bootstrap: [AppComponent]
})
export class AppModule {

  constructor(
    store: Store<AppRouterState>,
    persistantStateHookManager: PersistantStateHookManager
  ) {

    /**
     * Watch for campaignId to change and emit an event
     * when it is changed.
     */
    store.select(
      selectQueryParam('campaignId')
    ).pipe(
      skip(1),
      pairwise()
    ).subscribe(([_prev, next]) => {
      store.dispatch(CAMPAIGN_ID_CHANGED({ campaignId: next }));
    });

    persistantStateHookManager.attachHooks();
  }
}
